import { UserGroupService } from '@/app/platform/platform-data/services/types';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { ErrorInformation } from '@shared/models/error/error-information';
import { PlatformUserGroupService } from '@data/_generated-api/platform-api/services/platform-user-group.service';
import { err, ok } from '@common/result/Result';
import Morphism from 'morphism';
import { Injectable } from '@angular/core';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { baseFeatureId } from '@/app/constants';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { PageFeature } from '@ervo/page-framework';
import { FeatureAuthorizationEntity } from '@/app/platform/platform-data/models/feature-authorization.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';
import { AsyncResult } from '@common/result/asyncResult';

@Injectable()
export class UserGroupFacade implements UserGroupService {
  constructor(private readonly apiService: PlatformUserGroupService) {}

  async create(model: UserGroupEntity): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroups({
        body: model.creationModel
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async delete(model: UserGroupEntity): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService.deleteUsergroupsId({ id: model.deleteKey }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async getAll(...args: any[]): AsyncResult<UserGroupEntity[], ErrorInformation> {
    const result = await this.apiService.postUsergroupsQueryDatasource({ body: {} }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const userGroups = Morphism.map(UserGroupEntity, result.value.items);
    return ok(userGroups);
  }

  async update(model: UserGroupEntity): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService.putUsergroups({ body: model.editingModel }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async getLinkedAppIds(id: string): AsyncResult<string[], ErrorInformation> {
    const result = await this.apiService
      .getUsergroupsUsergroupidQueryAuthorizedapps({
        userGroupId: id
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    return ok([result.value].flat());
  }

  async linkAppToUserGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroupsActionAddpagefeature({
        body: {
          navigationItemId: app.rootNavigationItem.id,
          pageFeatureId: baseFeatureId,
          userGroupId: userGroup.id,
          pageFeatureName: 'BASE'
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async unlinkAppFromUserGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .deleteUsergroupsUsergroupidActionRemoveappAppid({
        userGroupId: userGroup.id,
        appId: app.id
      })
      .toPromise();
    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async authorizeNavigationItem(
    userGroup: UserGroupEntity,
    navigationItem: NavigationItemEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroupsActionAddpagefeature({
        body: {
          pageFeatureName: 'BASE',
          userGroupId: userGroup.id,
          pageFeatureId: baseFeatureId,
          navigationItemId: navigationItem.id
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const group = Morphism.map(UserGroupEntity, result.value);
    return ok(group);
  }

  async authorizePageFeature(
    group: UserGroupEntity,
    navigationItemId: string,
    feature: PageFeature
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroupsActionAddpagefeature({
        body: {
          pageFeatureName: feature.name,
          navigationItemId,
          pageFeatureId: feature.featureId,
          userGroupId: group.id
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async unAuthorizeNavigationItem(
    group: UserGroupEntity,
    navigationItem: NavigationItemEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const { id } = group.findBaseAuthorization(navigationItem);

    const result = await this.apiService
      .deleteUsergroupsActionRemovepagefeatureId({ id })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value);
    return ok(g);
  }

  async getById(id: string): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroupsQueryDatasource({
        body: {
          filterOptionGroup: {
            filterOptions: [
              {
                propertyName: 'id',
                value: id,
                operator: 'GUIDEQUALS'
              }
            ],
            filterOptionGroups: [],
            logicalOperator: ''
          }
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const g = Morphism.map(UserGroupEntity, result.value.items[0]);
    return ok(g);
  }

  async unAuthorizePageFeature({
    id
  }: FeatureAuthorizationEntity): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .deleteUsergroupsActionRemovepagefeatureId({ id })
      .toPromise();
    if (!result.isValid) {
      return err();
    }

    const group = Morphism.map(UserGroupEntity, result.value);
    return ok(group);
  }

  async addMember(
    group: UserGroupEntity,
    user: UserAccountEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .postUsergroupsActionAdduseraccount({
        body: {
          userAccountId: user.id,
          userGroupId: group.id
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const userGroup = Morphism.map(UserGroupEntity, result.value);
    return ok(userGroup);
  }

  async removeMember(
    group: UserGroupEntity,
    user: UserAccountEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation> {
    const result = await this.apiService
      .deleteUsergroupsUsergroupidActionRemoveuseraccountUseraccountid({
        userGroupId: group.id,
        userAccountId: user.id
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const userGroup = Morphism.map(UserGroupEntity, result.value);
    return ok(userGroup);
  }
}
